<template>
  <ListItemsWithTableLayout>
    <template #body>
      <HandlerAccessDenied :is-denied="isDenied">
        <TopPreSalePanel
          :name-dashbords="pageTitle"
          :is-dashboard-selected="isDashboardSelected"
          @expand-dashboard="handleExpandDashboard"
        ></TopPreSalePanel>
        <DashboardsViewForm
          v-if="selectedWorkbookId && selectedDashboardId"
          :key="`${selectedWorkbookId}-${selectedDashboardId}`"
          ref="dashboardsFormRef"
          :store="workbooksStore"
          type="own_workbook"
          :hide-expand-button="true"
          :default-workbook="selectedWorkbookId"
          :default-dashboard="selectedDashboardId"
          @dashboard-selected="updateDashboardSelection"
        ></DashboardsViewForm>
        <div v-else-if="!isLoading" class="empty-dashboard-state form-block">
          Для выбранного воркбука не найдено доступных дашбордов.
        </div>
      </HandlerAccessDenied>
    </template>
  </ListItemsWithTableLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import ListItemsWithTableLayout from "@/widgets/layouts/ListItemsWithTableLayout.vue";
import HandlerAccessDenied from "@/shared/base/HandlerAccessDenied.vue";
import TopPreSalePanel from "@/entities/analytics/dashboards/TopPreSalePanel.vue";
import DashboardsViewForm from "@/entities/analytics/dashboards/DashboardsViewForm.vue";

import { useAuthStore } from "@/store/auth/login";
import { useOwnWorkbooksStore } from "@/store/catalog_data/dashboards";
import { Workbook } from "@/store/analytics/dasboards";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const workbooksStore = useOwnWorkbooksStore();

const dashboardsFormRef = ref();
const isDashboardSelected = ref(false);
const isLoading = ref(false);
const selectedWorkbook = ref<Workbook | null>(null);

const isDenied = computed(() => {
  return authStore.isDeniedForPermissions(
    ["system:read", "system:edit"],
    ["analytics:read", "analytics:edit"]
  );
});

const pageTitle = computed(() => {
  return selectedWorkbook.value?.dashboards?.[0]?.name || "Демо-дашборды";
});

const selectedWorkbookId = computed(() => {
  return selectedWorkbook.value?.id || null;
});

const selectedDashboardId = computed(() => {
  return selectedWorkbook.value?.dashboards?.[0]?.id || null;
});

const updateDashboardSelection = (selected: boolean) => {
  isDashboardSelected.value = selected;
};

const handleExpandDashboard = () => {
  if (dashboardsFormRef.value) {
    dashboardsFormRef.value.expandDashboard();
  }
};

const resolveWorkbook = async () => {
  isLoading.value = true;
  try {
    await workbooksStore.getItems("", [], true, false, 0, 50, true);

    const availableWorkbooks = workbooksStore.itemList.filter(
      (item) => item.dashboards && item.dashboards.length > 0
    );

    if (availableWorkbooks.length === 0) {
      selectedWorkbook.value = null;
      return;
    }

    const routeWorkbookId = Number(route.params.workbookId);
    const matchedWorkbook = Number.isFinite(routeWorkbookId)
      ? availableWorkbooks.find((item) => item.id === routeWorkbookId)
      : undefined;

    const nextWorkbook = matchedWorkbook || availableWorkbooks[0];
    selectedWorkbook.value = nextWorkbook;

    if (!matchedWorkbook || String(route.params.workbookId) !== String(nextWorkbook.id)) {
      router.replace({
        name: "demoDashboards",
        params: { workbookId: nextWorkbook.id },
      });
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(resolveWorkbook);

watch(
  () => route.params.workbookId,
  () => {
    resolveWorkbook();
  }
);
</script>

<style scoped lang="scss">
.empty-dashboard-state {
  margin-top: 77px;
  padding: 16px;
}
</style>
